import type { AllocationData } from '@/app/models/avaliablePercent';
import { useDeleteAvaliablePercentMutation } from '@/app/services/avaliablePercetApi';
import formatterPercentualValue from '@/app/utils/percentualUtils';
import TruncatedTextWithTooltip from '@/app/utils/truncatedTextWithTooltip';
import StandardDialog from '@/components/common/StandardDialog';
import { TableCell, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ActionMenu, { ItemAction } from '../common/ActionMenu';
import DataTable, { bodyCellSx, Column } from '../common/DataTable';

interface GeneralTableProps {
  rows: AllocationData[];
  setDrawerOpen: (open: boolean) => void;
  setSelectedItem: (p: AllocationData) => void;
}

export default function AvaliablePercentageTable(props: GeneralTableProps) {
  const { rows, setDrawerOpen, setSelectedItem } = props;
  const { t } = useTranslation(['avaliablePercentage', 'common']);
  const [branchCodeToDelete, setBranchCodeToDelete] = useState<string | null>(null);
  const [openSaveDialog, setOpenSaveDialog] = useState(false);

  const [deleteAvaliablePercent] = useDeleteAvaliablePercentMutation();
  const theme = useTheme();

  const handleDelete = (branchCode: string) => {
    deleteAvaliablePercent(branchCode);
  };

  const columns: Column[] = [
    {
      key: 'region',
      label: t('region'),
      width: theme.spacing(2.5),
    },
    {
      key: 'percentage',
      label: t('total.percentage'),
      align: 'center',
    },
    {
      key: 'employees',
      label: t('employees'),
    },
    {
      key: 'actions',
      label: t('actions'),
      align: 'right',
    },
  ];

  const renderRow = (row: AllocationData) => (
    <>
      <TableCell sx={{ ...bodyCellSx, width: theme.spacing(40) }}>{row.branchName}</TableCell>
      <TableCell sx={bodyCellSx} align="center">
        {formatterPercentualValue(row.percent || '')}
      </TableCell>
      <TableCell sx={bodyCellSx}>
        <TruncatedTextWithTooltip
          value={`${row.badges}`}
          size={37}
          sx={{
            fontSize: 'body2.fontSize',
            textDecoration: 'none',
          }}
        />
      </TableCell>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
        <ActionMenu>
          <ItemAction
            text={t('common:edit') as string}
            actionType="edit"
            onClick={() => {
              setSelectedItem(row);
              setDrawerOpen(true);
            }}
          />
          <ItemAction
            text={t('common:delete') as string}
            actionType="delete"
            onClick={() => {
              setBranchCodeToDelete(row.branchCode);
              setOpenSaveDialog(true);
            }}
          />
        </ActionMenu>
      </TableCell>
    </>
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={rows}
        renderRow={renderRow}
        emptyMessage={t('common:no.results.found')}
        getRowKey={(item) => item.branchCode}
        maxHeight={theme.spacing(50)}
        stickyHeader
        size="small"
      />

      <StandardDialog
        title={t('confirm.delete.title')}
        contentText={t('confirm.delete.message')}
        open={openSaveDialog}
        onClose={() => setOpenSaveDialog(false)}
        onConfirm={() => {
          if (branchCodeToDelete) {
            handleDelete(branchCodeToDelete);
          }
          setOpenSaveDialog(false);
          setBranchCodeToDelete(null);
        }}
        cancelText={t('common:no')}
        confirmText={t('common:yes')}
      />
    </>
  );
}
